import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { HeroNetworkScan, IconCancel } from '../icons/HvacIcons';
import './hv-modal.css';

interface ClientInterfaceListItem {
  id: string;
  name: string;
  ip: string;
  netmask: string | null;
  mac: string | null;
  kind: string;
  isUp: boolean;
  lastSeenAt: string;
}

interface FieldClientListItem {
  id: string;
  mqttClientName: string;
  displayName: string | null;
  hardwareKind: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  interfaces: ClientInterfaceListItem[];
}

interface ListClientsResponse {
  clients: FieldClientListItem[];
}

interface Props {
  plantId: string;
  onTriggered(correlationId: string, triggeredAtIso: string | null): void;
  onClose(): void;
}

/**
 * Netzwerk-Scan-Trigger mit Interface-Auswahl. Zeigt alle vom Field-
 * Client per Heartbeat gemeldeten Interfaces und lässt den Operator
 * gezielt auswählen welche gescannt werden sollen. Default = alle
 * "up"-Interfaces vorausgewählt.
 *
 * Wenn (a) noch kein Heartbeat vom Client vorliegt oder (b) der Client
 * keine Interfaces gemeldet hat, fällt der Modal auf einen "einfachen"
 * Modus zurück: kein Filter, Server broadcastet ohne interfaceNames,
 * Client scannt seine Default-Interface-Konfiguration.
 */
export function TriggerScanModal({ plantId, onTriggered, onClose }: Props) {
  const clientsState = useAsync(
    (signal) => api.get<ListClientsResponse>(`/api/plants/${plantId}/clients`, signal),
    [plantId],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  const [firing, setFiring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allInterfaces = useMemo(() => {
    if (clientsState.status !== 'ok') return [];
    const rows: Array<{ client: FieldClientListItem; iface: ClientInterfaceListItem }> = [];
    for (const client of clientsState.data.clients) {
      for (const iface of client.interfaces) rows.push({ client, iface });
    }
    return rows;
  }, [clientsState]);

  // Vorauswahl: alle Interfaces die "up" sind (und nicht docker/loopback).
  useEffect(() => {
    if (initialized || clientsState.status !== 'ok') return;
    const next = new Set<string>();
    for (const { iface } of allInterfaces) {
      if (iface.isUp) next.add(iface.name);
    }
    setSelected(next);
    setInitialized(true);
  }, [allInterfaces, initialized, clientsState]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  function toggle(name: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }

  async function fire() {
    setFiring(true);
    setError(null);
    try {
      // No interfaces reported yet → send empty body (server broadcasts
      // without a filter and the client uses its default interface config).
      const body = allInterfaces.length === 0
        ? {}
        : { interfaceNames: Array.from(selected) };
      const res = await api.post<{ correlationId: string; triggeredAt?: string }>(
        `/api/plants/${plantId}/scan/trigger`,
        body,
      );
      onTriggered(res.correlationId, res.triggeredAt ?? null);
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string; detail?: string })?.error
          ?? (e.body as { detail?: string })?.detail;
        setError(bodyMsg ?? `Scan-Trigger fehlgeschlagen (${e.status}).`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler beim Trigger.');
      }
      setFiring(false);
    }
  }

  const hasInterfaces = allInterfaces.length > 0;
  const canFire = !firing && (!hasInterfaces || selected.size > 0);

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Netzwerk-Scan starten</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schliessen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__hero">
            <HeroNetworkScan size={72} style={{ color: 'var(--accent)' }} />
          </div>
          {clientsState.status === 'loading' && <p class="muted">Lade Field-Clients…</p>}
          {clientsState.status === 'error' && (
            <p class="hv-modal__error">Clients-Liste konnte nicht geladen werden.</p>
          )}
          {clientsState.status === 'ok' && !hasInterfaces && (
            <>
              <p>
                Für diese Anlage ist noch kein Field-Client mit Interface-Heartbeat
                bekannt. Der Server sendet den Trigger trotzdem — Clients nutzen dann
                ihre Konfiguration (<code class="mono">NetworkScanner:InterfaceIps</code>)
                oder scannen alle up-Interfaces.
              </p>
              <p class="muted">
                Kommt in wenigen Minuten der erste Heartbeat rein, kannst du hier
                gezielt Interfaces wählen.
              </p>
            </>
          )}
          {clientsState.status === 'ok' && hasInterfaces && (
            <>
              <p class="muted">
                Wähle die Interfaces, die abgetastet werden sollen. Alle "up"-Interfaces
                sind vorausgewählt. Der Scanner probt TCP&nbsp;(502, 4840, 8443, 80/443)
                + BACnet-Who-Is UDP&nbsp;47808 pro gewähltem Interface.
              </p>
              {clientsState.data.clients.map((client) => (
                <div class="hv-modal__field" key={client.id}>
                  <label>
                    {client.displayName ?? client.mqttClientName}
                    {client.hardwareKind && (
                      <span class="muted"> — {client.hardwareKind}</span>
                    )}
                  </label>
                  <div class="hv-scan-modal__ifaces">
                    {client.interfaces.map((iface) => {
                      const checked = selected.has(iface.name);
                      const disabledLabel = !iface.isUp ? ' (down)' : '';
                      return (
                        <label key={iface.id} class="hv-scan-modal__iface">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(iface.name)}
                            disabled={!iface.isUp}
                          />
                          <span class="mono">{iface.name}</span>
                          <span class="mono muted">{iface.ip}</span>
                          {iface.mac && <span class="mono muted">{iface.mac}</span>}
                          <span class="muted">{iface.kind}{disabledLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <button type="button" class="hv-btn" onClick={onClose} disabled={firing}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button type="button" class="hv-btn hv-btn--primary" onClick={fire} disabled={!canFire}>
            {firing
              ? 'Sende…'
              : hasInterfaces
                ? `Scan starten (${selected.size} Interface${selected.size === 1 ? '' : 's'})`
                : 'Scan starten'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
